import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExport, faFileExcel, faEye, faCloud, faArchive } from '@fortawesome/free-solid-svg-icons';

const AuditLogPage = () => {
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });
  const [filters, setFilters] = useState({
    action_category: '',
    outcome: '',
    start_date: '',
    end_date: '',
    search: ''
  });
  const [selectedLog, setSelectedLog] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showS3Dropdown, setShowS3Dropdown] = useState(false);
  const [showCreateArchiveModal, setShowCreateArchiveModal] = useState(false);
  const [archiveStartDate, setArchiveStartDate] = useState('');
  const [archiveEndDate, setArchiveEndDate] = useState('');
  const [archiveReason, setArchiveReason] = useState('');
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [archiveMessage, setArchiveMessage] = useState('');

  const fetchAuditLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const token = sessionStorage.getItem('token');

      const params = new URLSearchParams();
      params.append('page', pagination.page);
      params.append('limit', pagination.limit);

      if (filters.action_category) params.append('action_category', filters.action_category);
      if (filters.outcome) params.append('outcome', filters.outcome);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.search) params.append('search', filters.search);

      const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/admin/audit-logs?${params}`;
      console.log('🔍 [AuditLog] Fetching from: ', apiUrl);
      console.log('🔍 [AuditLog] Filters applied:', filters);

      const response = await axios.get(apiUrl, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('✅ [AuditLog] Response:', response.data);
      console.log('📊 [AuditLog] Total logs:', response.data.data.logs.length);
      console.log('📊 [AuditLog] Categories found:', [...new Set(response.data.data.logs.map(log => log.action_category))]);
      console.log('📊 [AuditLog] Actions found:', [...new Set(response.data.data.logs.map(log => log.action_verb))]);

      setAuditLogs(response.data.data.logs);
      setPagination(response.data.data.pagination);
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching audit logs');
      console.error('Error fetching audit logs:', err);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.limit]);

  // Fetch audit logs when filters or pagination changes
  useEffect(() => {
    fetchAuditLogs();
  }, [fetchAuditLogs]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    } catch (e) {
      return 'Invalid Date';
    }
  };

  const getOutcomeBadgeClass = (outcome) => {
    return outcome === 'success' 
      ? 'inline-block px-2 py-1 text-xs font-semibold text-white bg-green-500 rounded'
      : 'inline-block px-2 py-1 text-xs font-semibold text-white bg-red-500 rounded';
  };

  const getCategoryBadgeClass = (category) => {
    const colors = {
      'authentication': 'bg-blue-500',
      'authorization': 'bg-yellow-500',
      'data': 'bg-purple-500',
      'configuration': 'bg-gray-500',
      'user_lifecycle': 'bg-indigo-500',
      'financial': 'bg-red-600'
    };
    return `inline-block px-2 py-1 text-xs font-semibold text-white ${colors[category] || 'bg-gray-500'} rounded`;
  };

  const isFXAction = (actionVerb) => {
    const fxActions = ['FX_RATE_FETCH', 'FX_MARKUP_UPDATE', 'FX_MANUAL_REFRESH', 'FX_SYSTEM_TOGGLE', 'CURRENCY_CHANGE'];
    return fxActions.some(action => actionVerb?.toUpperCase().includes(action));
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleSearchChange = (e) => {
    const { value } = e.target;
    setFilters(prev => ({
      ...prev,
      search: value
    }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleRecordsPerPageChange = (e) => {
    const newLimit = Number(e.target.value);
    setPagination(prev => ({ ...prev, limit: newLimit, page: 1 }));
  };

  const handleResetFilters = () => {
    setFilters({
      action_category: '',
      outcome: '',
      start_date: '',
      end_date: '',
      search: ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleViewDetails = (log) => {
    setSelectedLog(log);
    setShowDetails(true);
  };

  const handleCloseDetails = () => {
    setShowDetails(false);
    setSelectedLog(null);
  };

  const handleExport = async (format) => {
    try {
      const token = sessionStorage.getItem('token');
      const params = new URLSearchParams();

      if (filters.action_category) params.append('action_category', filters.action_category);
      if (filters.outcome) params.append('outcome', filters.outcome);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.search) params.append('search', filters.search);

      const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/admin/audit-logs/export?${params}`;

      const response = await axios.post(apiUrl, {}, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit-logs.${format === 'excel' ? 'xlsx' : 'csv'}`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      setShowExportDropdown(false);
    } catch (err) {
      console.error('Error exporting audit logs:', err);
      alert('Error exporting audit logs');
    }
  };

  const handleDownloadFromS3 = async (format) => {
    try {
      const token = sessionStorage.getItem('token');
      
      // First, fetch the list of archives to get the latest one
      const listUrl = `${process.env.REACT_APP_API_BASE_URL}/admin/audit-logs/s3/archives?limit=1&offset=0`;
      const listResponse = await axios.get(listUrl, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const archives = listResponse.data.data?.archives || [];
      
      if (archives.length === 0) {
        alert('No archived audit logs found in S3');
        return;
      }

      const latestArchive = archives[0];
      const archiveId = latestArchive.id;

      // Now download the archive in the requested format
      const downloadUrl = `${process.env.REACT_APP_API_BASE_URL}/admin/audit-logs/s3/archives/${archiveId}/download?format=${format}`;

      const response = await axios.get(downloadUrl, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit-archive-s3-${new Date().toISOString().split('T')[0]}.${format === 'csv' ? 'csv' : format === 'excel' ? 'xlsx' : 'json'}`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      setShowS3Dropdown(false);
    } catch (err) {
      console.error('Error downloading from S3:', err);
      alert('Error downloading from S3: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleCreateArchive = async () => {
    try {
      if (!archiveStartDate || !archiveEndDate) {
        alert('Please select both start and end dates');
        return;
      }

      setArchiveLoading(true);
      setArchiveMessage('');
      const token = sessionStorage.getItem('token');

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/admin/audit-logs/s3/archive`,
        {
          start_date: new Date(archiveStartDate).toISOString(),
          end_date: new Date(archiveEndDate).toISOString(),
          archive_reason: archiveReason || 'Manual archival'
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setArchiveMessage(`✅ Archive created successfully! (${response.data.data.record_count} logs archived)`);
        setArchiveStartDate('');
        setArchiveEndDate('');
        setArchiveReason('');
        setTimeout(() => {
          setShowCreateArchiveModal(false);
          setArchiveMessage('');
        }, 2000);
      }
    } catch (err) {
      console.error('Error creating archive:', err);
      setArchiveMessage(`❌ Error: ${err.response?.data?.message || err.message}`);
    } finally {
      setArchiveLoading(false);
    }
  };

  return (
    <div className="p-4 w-full h-full flex flex-col">
      {/* Header with Export Button - TOP RIGHT */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <span className="font-semibold text-gray-800">Configuration Audit Log</span>
        </div>
        <div className="flex flex-col sm:flex-row gap-2.5 items-stretch sm:items-center w-full md:w-auto">
          {/* Create Archive Button */}
          <button
            className="text-white h-9 w-full sm:w-[156px] py-2.5 px-4 text-[14px] font-['Bahnschrift',sans-serif] leading-[16.8px] bg-gradient-to-br from-[#4caf50] to-[#388e3c] border-transparent rounded shadow-none border inline-block mb-0 font-normal text-center whitespace-nowrap align-middle cursor-pointer select-none transition-colors duration-200 hover:bg-gradient-to-br hover:from-[#388e3c] hover:to-[#2e7d32]"
            onClick={() => setShowCreateArchiveModal(true)}
          >
            <FontAwesomeIcon icon={faArchive} /> Create Archive
          </button>

          {/* S3 Download Button */}
          <div className="relative w-full sm:w-auto">
            <button
              className="text-black h-9 w-full sm:w-[156px] py-2.5 px-4 text-[14px] font-['Bahnschrift',sans-serif] leading-[16.8px] bg-gradient-to-br from-[#ff9800] to-[#f57c00] border-transparent rounded shadow-none border inline-block mb-0 font-normal text-center whitespace-nowrap align-middle cursor-pointer select-none transition-colors duration-200 hover:bg-gradient-to-br hover:from-[#f57c00] hover:to-[#e65100]"
              onClick={() => setShowS3Dropdown(!showS3Dropdown)}
            >
              <FontAwesomeIcon icon={faCloud} /> S3 Download
            </button>
            {showS3Dropdown && (
              <div className="absolute top-full right-0 bg-gradient-to-br from-[#ff9800] to-[#f57c00] border border-transparent rounded py-2.5 px-0 shadow-[0_2px_8px_rgba(0,0,0,0.15)] z-10 min-w-[155px] mt-[5px] overflow-hidden">
                <div onClick={() => handleDownloadFromS3('csv')} className="py-3 px-4 cursor-pointer text-[14px] text-white font-normal transition-colors duration-200 flex items-center gap-2.5 hover:bg-gradient-to-br hover:from-[#f57c00] hover:to-[#e65100]">
                  <FontAwesomeIcon icon={faFileExport} /> CSV
                </div>
                <div onClick={() => handleDownloadFromS3('excel')} className="py-3 px-4 cursor-pointer text-[14px] text-white font-normal transition-colors duration-200 flex items-center gap-2.5 hover:bg-gradient-to-br hover:from-[#f57c00] hover:to-[#e65100]">
                  <FontAwesomeIcon icon={faFileExcel} /> Excel
                </div>
              </div>
            )}
          </div>

          {/* Export Button */}
          <div className="relative w-full sm:w-auto">
            <button
              className="text-black h-9 w-full sm:w-[156px] py-2.5 px-4 text-[14px] font-['Bahnschrift',sans-serif] leading-[16.8px] bg-gradient-to-br from-[#ffeb00] to-[#ffd200] border-transparent rounded shadow-none border inline-block mb-0 font-normal text-center whitespace-nowrap align-middle cursor-pointer select-none transition-colors duration-200 hover:bg-gradient-to-br hover:from-[#ffd200] hover:to-[#ffb300]"
              onClick={() => setShowExportDropdown(!showExportDropdown)}
            >
              <FontAwesomeIcon icon={faFileExport} /> Export
            </button>
            {showExportDropdown && (
              <div className="absolute top-full right-0 bg-gradient-to-br from-[#ffeb00] to-[#ffd200] border border-transparent rounded py-2.5 px-0 shadow-[0_2px_8px_rgba(0,0,0,0.15)] z-10 min-w-[155px] mt-[5px] overflow-hidden">
                <div onClick={() => handleExport('excel')} className="py-3 px-4 cursor-pointer text-[14px] text-black font-normal transition-colors duration-200 flex items-center gap-2.5 hover:bg-gradient-to-br hover:from-[#ffd200] hover:to-[#ffb300]">
                  <FontAwesomeIcon icon={faFileExcel} /> Excel
                </div>
                <div onClick={() => handleExport('csv')} className="py-3 px-4 cursor-pointer text-[14px] text-black font-normal transition-colors duration-200 flex items-center gap-2.5 hover:bg-gradient-to-br hover:from-[#ffd200] hover:to-[#ffb300]">
                  <FontAwesomeIcon icon={faFileExport} /> CSV
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* FX Audit Logs Info Banner */}
      {/* <div className="mb-4 p-4 bg-blue-50 border-l-4 border-blue-400 rounded">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <i className="fa fa-info-circle text-blue-400 text-lg"></i>
          </div>
          <div className="ml-3">
            <h4 className="text-sm font-semibold text-blue-800 mb-1">FX Audit Logs</h4>
            <p className="text-xs text-blue-700">
              FX-related events appear here with category "Configuration". Look for actions like: 
              <span className="font-semibold"> FX_RATE_FETCH, FX_MARKUP_UPDATE, FX_MANUAL_REFRESH, CURRENCY_CHANGE</span>.
              FX actions are marked with a green <span className="inline-block px-1.5 py-0.5 text-xs font-semibold text-white bg-green-600 rounded">FX</span> badge.
            </p>
            <p className="text-xs text-blue-600 mt-1">
              💡 Tip: Filter by "Configuration" category to see FX settings changes, or search for "FX" to find all FX-related logs.
            </p>
          </div>
        </div>
      </div> */}

      {/* White Box Container */}
      <div className="bg-white rounded shadow-sm border border-gray-100 flex-1 flex flex-col pt-4 overflow-hidden">
        {/* Filters Section - INSIDE the white box */}
        <div className="px-4 pb-4 border-b border-gray-200">
          <div className="text-sm font-semibold text-gray-700 mb-3">Filters</div>
          
          {/* Search and Filter Buttons Row */}
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center mb-3">
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600 font-normal">Search:</label>
              <input
                type="text"
                placeholder="Search by actor, action, entity..."
                value={filters.search}
                onChange={handleSearchChange}
                className="px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-500"
              />
            </div>

            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600 font-normal">Category:</label>
              <select
                name="action_category"
                value={filters.action_category}
                onChange={handleFilterChange}
                className="px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-500"
              >
                <option value="">All Categories</option>
                <option value="authentication">Authentication</option>
                <option value="authorization">Authorization</option>
                <option value="data">Data</option>
                <option value="configuration">Configuration</option>
                <option value="user_lifecycle">User Lifecycle</option>
                <option value="financial">Financial</option>
              </select>
            </div>

            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600 font-normal">Status:</label>
              <select
                name="outcome"
                value={filters.outcome}
                onChange={handleFilterChange}
                className="px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-500"
              >
                <option value="">All Status</option>
                <option value="success">Success</option>
                <option value="failure">Failure</option>
              </select>
            </div>

            <button
              onClick={() => {}}
              className="px-4 py-2 text-sm font-semibold text-white bg-yellow-500 rounded hover:bg-yellow-600 transition-colors"
            >
              Filter
            </button>

            <button
              onClick={handleResetFilters}
              className="px-4 py-2 text-sm font-semibold text-gray-700 bg-gray-300 rounded hover:bg-gray-400 transition-colors"
            >
              Reset
            </button>
          </div>

          {/* Date Range Row */}
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600 font-normal">Start Date:</label>
              <input
                type="datetime-local"
                name="start_date"
                value={filters.start_date}
                onChange={handleFilterChange}
                className="px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-500"
              />
            </div>

            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600 font-normal">End Date:</label>
              <input
                type="datetime-local"
                name="end_date"
                value={filters.end_date}
                onChange={handleFilterChange}
                className="px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Table Section */}
        <div className="flex-1 overflow-auto">
          {error && (
            <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded m-4">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-gray-500">Loading audit logs...</div>
            </div>
          ) : auditLogs.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-gray-500">No audit logs found</div>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
              <table className="w-full border-collapse min-w-[900px]">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-b border-gray-200">#</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-b border-gray-200">Timestamp</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-b border-gray-200">Actor</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-b border-gray-200">Action</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-b border-gray-200">Category</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-b border-gray-200">Entity</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-b border-gray-200">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-b border-gray-200">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {auditLogs.map((log, index) => (
                    <tr key={log.id} className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-700">{(pagination.page - 1) * pagination.limit + index + 1}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{formatDate(log.timestamp)}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        <div className="font-semibold">{log.actor_name || 'System'}</div>
                        <div className="text-xs text-gray-500">{log.actor_role}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {log.action_verb}
                        {isFXAction(log.action_verb) && (
                          <span className="ml-2 inline-block px-2 py-0.5 text-xs font-semibold text-white bg-green-600 rounded">
                            FX
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className={getCategoryBadgeClass(log.action_category)}>
                          {log.action_category}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {log.target_entity_type}
                        {log.target_entity_id && ` #${log.target_entity_id}`}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className={getOutcomeBadgeClass(log.outcome)}>
                          {log.outcome.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-center">
                        <button
                          onClick={() => handleViewDetails(log)}
                          className="inline-flex items-center justify-center w-8 h-8 text-blue-600 hover:bg-blue-100 rounded transition-colors"
                          title="View Details"
                        >
                          <FontAwesomeIcon icon={faEye} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>

              {/* Pagination */}
              {auditLogs.length > 0 && (
                <div className="px-4 py-4 border-t border-gray-200 flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <label className="text-sm text-gray-600">Display</label>
                    <select
                      value={pagination.limit}
                      onChange={handleRecordsPerPageChange}
                      className="px-2 py-1 text-sm border border-gray-300 rounded"
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <label className="text-sm text-gray-600">Records</label>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      disabled={pagination.page === 1}
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                      className="px-3 py-1 text-sm font-semibold text-white bg-blue-600 rounded hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                    >
                      Previous
                    </button>
                    <span className="text-sm text-gray-600 whitespace-nowrap">
                      Page {pagination.page} of {pagination.pages} (Total: {pagination.total})
                    </span>
                    <button
                      disabled={pagination.page === pagination.pages}
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                      className="px-3 py-1 text-sm font-semibold text-white bg-blue-600 rounded hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Details Modal */}
      {showDetails && selectedLog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={handleCloseDetails}>
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="bg-white px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">Audit Log Details</h2>
              <button
                onClick={handleCloseDetails}
                className="text-gray-400 hover:text-gray-600 text-3xl leading-none font-light"
              >
                ×
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto flex-1">
              <div className="grid grid-cols-2 gap-x-8 gap-y-6">
                {/* Left Column */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">ID</label>
                  <p className="text-base text-gray-900">{selectedLog.id}</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Timestamp</label>
                  <p className="text-base text-gray-900">{formatDate(selectedLog.timestamp)}</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Actor</label>
                  <p className="text-base text-gray-900">{selectedLog.actor_name || 'System'} ({selectedLog.actor_role || 'User'})</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Action</label>
                  <p className="text-base text-gray-900">{selectedLog.action_verb}</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Category</label>
                  <span className={getCategoryBadgeClass(selectedLog.action_category)}>
                    {selectedLog.action_category?.toUpperCase()}
                  </span>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Entity</label>
                  <p className="text-base text-gray-900">
                    {selectedLog.target_entity_type || 'N/A'}
                    {selectedLog.target_entity_id && ` #${selectedLog.target_entity_id}`}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
                  <span className={getOutcomeBadgeClass(selectedLog.outcome)}>
                    {selectedLog.outcome?.toUpperCase()}
                  </span>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">IP Address</label>
                  <p className="text-base text-gray-900">{selectedLog.ip_address || 'N/A'}</p>
                </div>
              </div>

              {/* Before Values */}
              {selectedLog.before_values && (() => {
                try {
                  const beforeData = typeof selectedLog.before_values === 'string' 
                    ? JSON.parse(selectedLog.before_values) 
                    : selectedLog.before_values;
                  
                  if (beforeData && Object.keys(beforeData).length > 0) {
                    return (
                      <div className="mt-6">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Before Values</label>
                        <div className="bg-gray-50 border border-gray-200 rounded p-4">
                          <pre className="text-sm text-gray-800 whitespace-pre-wrap">
                            {JSON.stringify(beforeData, null, 2)}
                          </pre>
                        </div>
                      </div>
                    );
                  }
                } catch (e) {
                  return null;
                }
                return null;
              })()}

              {/* After Values */}
              {selectedLog.after_values && (() => {
                try {
                  const afterData = typeof selectedLog.after_values === 'string' 
                    ? JSON.parse(selectedLog.after_values) 
                    : selectedLog.after_values;
                  
                  if (afterData && Object.keys(afterData).length > 0) {
                    return (
                      <div className="mt-6">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">After Values</label>
                        <div className="bg-gray-50 border border-gray-200 rounded p-4">
                          <pre className="text-sm text-gray-800 whitespace-pre-wrap">
                            {JSON.stringify(afterData, null, 2)}
                          </pre>
                        </div>
                      </div>
                    );
                  }
                } catch (e) {
                  return null;
                }
                return null;
              })()}

              {/* Metadata */}
              {selectedLog.metadata && (() => {
                try {
                  const metaData = typeof selectedLog.metadata === 'string' 
                    ? JSON.parse(selectedLog.metadata) 
                    : selectedLog.metadata;
                  
                  if (metaData && Object.keys(metaData).length > 0) {
                    return (
                      <div className="mt-6">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Metadata</label>
                        <div className="bg-gray-50 border border-gray-200 rounded p-4">
                          <pre className="text-sm text-gray-800 whitespace-pre-wrap">
                            {JSON.stringify(metaData, null, 2)}
                          </pre>
                        </div>
                      </div>
                    );
                  }
                } catch (e) {
                  return null;
                }
                return null;
              })()}

              {/* Error Message */}
              {selectedLog.error_message && (
                <div className="mt-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Error Message</label>
                  <div className="bg-red-50 border border-red-200 rounded p-4">
                    <p className="text-sm text-red-800">{selectedLog.error_message}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create Archive Modal */}
      {showCreateArchiveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowCreateArchiveModal(false)}>
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-gray-100 px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-lg font-bold text-gray-800">Create Archive</h2>
              <button
                onClick={() => setShowCreateArchiveModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
              >
                ×
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Start Date</label>
                <input
                  type="datetime-local"
                  value={archiveStartDate}
                  onChange={(e) => setArchiveStartDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">End Date</label>
                <input
                  type="datetime-local"
                  value={archiveEndDate}
                  onChange={(e) => setArchiveEndDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Archive Reason (Optional)</label>
                <input
                  type="text"
                  value={archiveReason}
                  onChange={(e) => setArchiveReason(e.target.value)}
                  placeholder="e.g., Monthly archival"
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500"
                />
              </div>
              {archiveMessage && (
                <div className={`p-3 rounded text-sm ${archiveMessage.includes('✅') ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                  {archiveMessage}
                </div>
              )}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowCreateArchiveModal(false)}
                  className="flex-1 px-4 py-2 text-sm font-semibold text-gray-700 bg-gray-300 rounded hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateArchive}
                  disabled={archiveLoading}
                  className="flex-1 px-4 py-2 text-sm font-semibold text-white bg-green-600 rounded hover:bg-green-700 disabled:bg-gray-400 transition-colors"
                >
                  {archiveLoading ? 'Creating...' : 'Create Archive'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditLogPage;
